import _extends from "@babel/runtime/helpers/esm/extends";
import { findLastStreamingPartIndex, updateMessageParts } from "./streamHelpers.js";
/**
 * Creates a self-contained text/reasoning delta buffer subsystem.
 *
 * Batches rapid text deltas into a single store update scheduled via `flushInterval` (ms).
 * Callers must invoke `flushPendingTextLikeDelta()` before any non-delta operation
 * and `clearPendingTextLikeDeltaTimer()` in the finally block.
 */
export function createTextDeltaBuffer(storeUnknown, ensureAssistantMessage, flushInterval) {
  const textPartIndexesByStreamId = new Map();
  const reasoningPartIndexesByStreamId = new Map();
  let pendingTextLikeDelta = null;
  let pendingTextLikeDeltaTimer = null;
  const clearPendingTextLikeDeltaTimer = () => {
    if (pendingTextLikeDeltaTimer) {
      clearTimeout(pendingTextLikeDeltaTimer);
      pendingTextLikeDeltaTimer = null;
    }
  };
  const resolveTextLikePartIndex = (partType, streamId, partIndexesByStreamId) => {
    const message = ensureAssistantMessage();
    const mappedIndex = partIndexesByStreamId.get(streamId);
    if (mappedIndex != null && message.parts[mappedIndex]?.type === partType) {
      return mappedIndex;
    }
    const fallbackIndex = findLastStreamingPartIndex(message.parts, partType);
    if (fallbackIndex !== -1) {
      partIndexesByStreamId.set(streamId, fallbackIndex);
      return fallbackIndex;
    }
    const nextIndex = message.parts.length;
    const nextPart = partType === 'text' ? {
      type: 'text',
      text: '',
      state: 'streaming'
    } : {
      type: 'reasoning',
      text: '',
      state: 'streaming'
    };
    storeUnknown.updateMessage(message.id, {
      parts: [...message.parts, nextPart]
    });
    partIndexesByStreamId.set(streamId, nextIndex);
    return nextIndex;
  };
  const applyTextLikeDelta = (partType, streamId, delta) => {
    const indexMap = partType === 'text' ? textPartIndexesByStreamId : reasoningPartIndexesByStreamId;
    const partIndex = resolveTextLikePartIndex(partType, streamId, indexMap);
    updateMessageParts(storeUnknown, ensureAssistantMessage().id, parts => {
      const currentPart = parts[partIndex];
      if (currentPart?.type !== partType) {
        return parts;
      }
      const nextParts = [...parts];
      nextParts[partIndex] = _extends({}, currentPart, {
        text: `${currentPart.text}${delta}`,
        state: 'streaming'
      });
      return nextParts;
    });
  };
  const flushPendingTextLikeDelta = () => {
    if (!pendingTextLikeDelta) {
      return;
    }
    const {
      partType,
      streamId,
      delta
    } = pendingTextLikeDelta;
    pendingTextLikeDelta = null;
    clearPendingTextLikeDeltaTimer();
    applyTextLikeDelta(partType, streamId, delta);
  };
  const scheduleTextLikeDelta = (partType, streamId, delta) => {
    if (pendingTextLikeDelta && (pendingTextLikeDelta.partType !== partType || pendingTextLikeDelta.streamId !== streamId)) {
      flushPendingTextLikeDelta();
    }
    if (!pendingTextLikeDelta) {
      pendingTextLikeDelta = {
        partType,
        streamId,
        delta
      };
      clearPendingTextLikeDeltaTimer();
      pendingTextLikeDeltaTimer = setTimeout(() => {
        flushPendingTextLikeDelta();
      }, flushInterval);
      return;
    }
    pendingTextLikeDelta = _extends({}, pendingTextLikeDelta, {
      delta: `${pendingTextLikeDelta.delta}${delta}`
    });
  };
  return {
    resolveTextLikePartIndex,
    applyTextLikeDelta,
    flushPendingTextLikeDelta,
    scheduleTextLikeDelta,
    clearPendingTextLikeDeltaTimer,
    textPartIndexesByStreamId,
    reasoningPartIndexesByStreamId
  };
}